import type { StackProps } from '@mui/material/Stack';
import Stack from '@mui/material/Stack';
import type { Theme } from '@mui/material/styles';
import type { TypographyProps } from '@mui/material/Typography';
import Typography from '@mui/material/Typography';
import isNumber from 'lodash/isNumber';
import React from 'react';

type MinWidthVariant = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';

export type KeyValuePairProps = {
  label: string | React.ReactNode;
  value: Readonly<string> | Readonly<React.ReactNode>;
  minWidth?: Readonly<MinWidthVariant | number>;
  height?: Readonly<string> | ((theme: Theme) => string);
  slotProps?: {
    stack?: StackProps;
    label?: TypographyProps;
  };
};

const labelWidth: { [size in MinWidthVariant]: (theme: Theme) => string } = {
  xsmall: theme => theme.spacing(16.25),
  small: theme => theme.spacing(21),
  medium: theme => theme.spacing(23.125),
  large: theme => theme.spacing(27.5),
  xlarge: theme => theme.spacing(32.5),
};

export function KeyValuePair({
  label,
  value,
  minWidth = 'medium',
  height,
  slotProps: { stack: stackProps, label: labelTypographyProps } = {},
}: KeyValuePairProps) {
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      flexWrap="nowrap"
      textTransform="none"
      paddingY={theme => theme.spacing(0.625)}
      gap={1}
      height={height}
      role="row"
      {...stackProps}
    >
      <Typography
        variant="highlight1"
        color="text.secondary"
        minWidth={isNumber(minWidth) ? minWidth : labelWidth[minWidth]}
        style={{ whiteSpace: 'pre-line' }}
        {...labelTypographyProps}
      >
        {label}
      </Typography>
      {typeof value === 'string' ? <Typography>{value}</Typography> : value}
    </Stack>
  );
}
